import React, { useCallback, useEffect, useState } from 'react';
import useSWR from 'swr';
import fetch from 'unfetch';
import decode from 'entity-decode';
import { Navigate, useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { HorizontalTable } from '@components/organisms';
import { dialogContentsState } from '@components/templates';
import { css } from '@emotion/react';
import { logos } from '@assets/logos';
import { icons } from '@assets/icons';
import { ADMIN_ACCESS_TOKEN_KEY, BASE_URL, PRODUCT_TITLE } from '../../../App';
import { ParsedWorkListItem } from '@pages/License/History';
import SearchButton from '@components/atoms/Inputs/Button/Search';
import { useSetRecoilState } from 'recoil';
import { Input, PrimaryButton, Sub1Button } from '@components/atoms';
import { LicenseInfoBody, LicenseIcon, ProductSection, NOTavailable, Divider, ContactBox } from './style';
import { InformationType } from '@models/license/licence';

const fetcher = (url: string, accessToken: string) =>
  fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  }).then((r) => {
    return r.json();
  });

export const getLicenseInfoString = (isUnlimited: boolean, fromStr: string, toStr: string) => {
  if (isUnlimited === null) {
    return 'Expired';
  }
  if (isUnlimited) {
    return 'Unlimited';
  } else {
    return (
      <>
        <b>{fromStr}</b>
        {toStr}
      </>
    );
  }
};

const InfoPage = () => {
  const cookies = new Cookies();
  const HAVE_TO_ALERT_LICENSE_EXPIRED_KEY = 'haveToAlertLicenseExpired'; // 알림을 7일간 받아야 하는지
  const VIEWED_ALERT_OF_LICENSE_EXPIRED_KEY = 'viewedAlertOfLicenseExpired'; // 알림을 받았던 적이 있는지
  const ONE_WEEK = 60 * 60 * 24 * 7;

  const random = React.useRef(Date.now());
  const [accessToken, setAccessToken] = useState('');
  const [shouldFetch, setShouldFetch] = useState(true);
  const [infoData, setInfoData] = useState<any>({});
  const navigate = useNavigate();
  const setDialogContents = useSetRecoilState(dialogContentsState);
  const [doNotShowLicenseLayer, setDoNotShowLicenseLayer] = useState(false);

  useEffect(() => {
    const newToken = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
    if (newToken) {
      setAccessToken(newToken);
    }
  }, []);

  // @FIXME 수정필요함
  const url = `api/license`;

  const showErrorMessage = useCallback(
    (
      type: 'error' | 'warning',
      header: string,
      message: string | React.JSX.Element,
      okBtnText?: string,
      goToAfterOk?: string,
      cancelBtnText?: string,
      goToAfterCancel?: string,
    ) => {
      setDialogContents({
        type: type,
        header: `${header}`,
        body: <>{message}</>,
        footer: (
          <>
            {cancelBtnText && (
              <Sub1Button
                styles={css`
                  border-color: #999999;
                  color: #999999;

                  :hover {
                    background: rgba(153, 153, 153, 0.2);
                    color: #999999;
                  }

                  :active {
                    background: #999999;
                    color: #ffffff;
                  }
                `}
                onClick={() => {
                  setDialogContents({ type: 'hidden' });
                  goToAfterCancel && navigate(goToAfterCancel);
                  setDoNotShowLicenseLayer(false);
                }}
              >
                {cancelBtnText}
              </Sub1Button>
            )}
            {okBtnText && (
              <PrimaryButton
                onClick={(e) => {
                  setDialogContents({ type: 'hidden' });
                  goToAfterOk && navigate(goToAfterOk);
                  setDoNotShowLicenseLayer(false);
                  setDoNotShow();
                }}
              >
                {okBtnText}
              </PrimaryButton>
            )}
          </>
        ),
      });
    },
    [doNotShowLicenseLayer],
  );

  const { data, error } = useSWR(shouldFetch && !!accessToken.length ? [{ url, accessToken }, random] : null);

  /**
   * 다시 보지 않기 상태 변경
   * @param e
   */
  const changeNotShowLayerState = (e: React.ChangeEvent<HTMLInputElement>) => {
    // 상태 변경
    setDoNotShowLicenseLayer(e.target.checked);
    // storage 정보 저장시 상태 값의 갱신을 위해 속성 값 추가
    const _target = document.getElementsByName('doNotShow');
    _target[0].setAttribute('checked', String(e.target.checked));
  };

  /**
   * licese 경고 레이어 do not show
   * @param e
   */
  const setDoNotShow = useCallback(() => {
    // storage 정보 저장시 상태 값의 갱신을 위해 속성 값 확인
    const _target = document.getElementsByName('doNotShow');
    if (_target[0].getAttribute('checked') === 'true') {
      localStorage.setItem('doNotShowLicenseIssue', 'true');
    }
  }, [doNotShowLicenseLayer]);

  useEffect(() => {
    if (data?.result) {
      setShouldFetch(false);
      setInfoData(data.result as InformationType);
    }
  }, [data]);

  if (error) {
    setShouldFetch(true);
    return <div>failed to load</div>;
  }

  if (data) {
    if (data.code === 'UNAUTHORIZED') {
      return <Navigate to={'/'} replace={true} />;
    }
    const _doNotShowStatus = localStorage.getItem('doNotShowLicenseIssue');

    //ACTIVE or WAITING 라이센스가 없는경우, 기점으로 7일간만
    if ((data.result as InformationType).id === null) {
      const haveToAlertLicenseExpired = cookies.get(HAVE_TO_ALERT_LICENSE_EXPIRED_KEY);
      const viewedAlertOfLicense = cookies.get(VIEWED_ALERT_OF_LICENSE_EXPIRED_KEY);

      if (!haveToAlertLicenseExpired && !viewedAlertOfLicense) {
        cookies.set(HAVE_TO_ALERT_LICENSE_EXPIRED_KEY, 'true', {
          // 알림을 봐야 하는 기간을 7일간으로 설정
          path: '/',
          expires: new Date(),
          maxAge: ONE_WEEK,
        });
        cookies.set(VIEWED_ALERT_OF_LICENSE_EXPIRED_KEY, 'true', {
          // 알림을 받았던 적이 있는지
          path: '/',
        });
      }

      if (haveToAlertLicenseExpired && infoData.companyName && !_doNotShowStatus) {
        showErrorMessage(
          'error',
          'License error : expired',
          <>
            {'The number of'} <span style={{ color: '#ff4c4c' }}>active accounts</span> {'has been exceeded.'}
            <br />
            {'Please deactive some accounts.'}
            <br />
            <br />
            <Input
              name={'doNotShow'}
              styles={css`
                justify-content: center;
              `}
              type={`checkbox`}
              onChange={(e) => changeNotShowLayerState(e)}
            >
              {`Don't show this message again.`}
            </Input>
          </>,
          'confirm',
          '',
          'Go to User account',
          '/permissions',
        );
      }
    } else {
      //활성화된 라이센스가 있는경우 알림 정보들(7일간 받아야 하는지, 알림을 받았던 적이 있는지) 제거
      cookies.remove(HAVE_TO_ALERT_LICENSE_EXPIRED_KEY);
      cookies.remove(VIEWED_ALERT_OF_LICENSE_EXPIRED_KEY);

      // 라이선스가 만료된 경우 혹은 임박한 경우, 라이센스 만료 30일전 or 잔여 횟수 10% 미만
      const willBeExpired =
        (!(data.result as InformationType).isUnlimitedCount &&
          ((data.result as InformationType).limitCount - (data.result as InformationType).usedCount) /
            (data.result as InformationType).limitCount <
            0.1) ||
        (!(data.result as InformationType).isUnlimitedPeriod &&
          Math.floor(
            (new Date((data.result as InformationType).toDate).getTime() - new Date().getTime()) /
              (24 * 60 * 60 * 1000),
          ) < 31 &&
          new Date((data.result as InformationType).toDate).getTime() - new Date().getTime() > 0);

      // 500?
      if (data.code === '500' || data.status !== 'SUCCESS') {
        if (!_doNotShowStatus) {
          showErrorMessage(
            'error',
            'License error : unknown',
            <>
              {'An unknown error has occurred'}
              <br />
              {'If the problem persists, contact your product company.'}
              <br />
              <br />
              <Input
                styles={css`
                  justify-content: center;
                `}
                type={`checkbox`}
                onChange={(e) => changeNotShowLayerState(e)}
              >
                {`Don't show this message again.`}
              </Input>
            </>,
            'Go to Support',
            '/support',
          );
        }
      } else if ((data.result as InformationType).status !== 'ACTIVE') {
        if (!_doNotShowStatus) {
          showErrorMessage(
            'error',
            'License error : expired',
            <>
              {'License is expired'}
              <br />
              {' Please contact your product company.'}
              <br />
              <br />
              <Input
                name={'doNotShow'}
                styles={css`
                  justify-content: center;
                `}
                type={`checkbox`}
                onChange={(e) => changeNotShowLayerState(e)}
              >
                {`Don't show this message again.`}
              </Input>
            </>,
            'confirm',
            '',
            'Go to Register',
            '/license/registration',
          );
        }
      } else if (
        (!(data.result as InformationType).isUnlimitedCount || !(data.result as InformationType).isUnlimitedPeriod) &&
        willBeExpired
      ) {
        if (!_doNotShowStatus) {
          showErrorMessage(
            'warning',
            'License is about to expire.',
            <>
              {'Contact your product company to resolve it.'}
              <Divider />
              <ContactBox>
                <img src={logos.heuron_logo} />
                <div>Heuron Co.,Ltd +82-2-795-8508</div>
              </ContactBox>
              <br />
              <br />
              <Input
                name={'doNotShow'}
                styles={css`
                  justify-content: center;
                `}
                type={`checkbox`}
                onChange={(e) => changeNotShowLayerState(e)}
              >
                {`Don't show this message again.`}
              </Input>
            </>,
            'confirm',
            '',
            'Go to License info',
            '/license',
          );
        }
      }
      // 활성 사용자수가 초과된 경우
      else if (
        !(data.result as InformationType).isUnlimitedUser &&
        (data.result as InformationType).activatedUserCount > (data.result as InformationType).limitUser
      ) {
        if (!_doNotShowStatus) {
          showErrorMessage(
            'error',
            'License error : active accounts',
            <>
              {'The number of'} <span style={{ color: '#ff4c4c' }}>active accounts</span> {'has been exceeded.'}
              <br />
              {'Please deactive some accounts.'}
              <br />
              <br />
              <Input
                styles={css`
                  justify-content: center;
                `}
                type={`checkbox`}
                onChange={(e) => changeNotShowLayerState(e)}
              >
                {`Don't show this message again.`}
              </Input>
            </>,
            'confirm',
            '',
            'Go to User account',
            '/permissions',
          );
        }
      } else {
        // 문제 없을 경우 license 레이어 다시보지 않기 초기화
        localStorage.removeItem('doNotShowLicenseIssue');
      }
    }
  }

  const moveToDetailItemPage = (item: any) => {
    navigate(`/license/history/detail/${item.id}`);
    // let licenseHistories: ParsedWorkListItem = {
    //   id: item.id,
    //   no: item.id,
    //   type: item.type,
    //   version: item.version,
    //   products: item.products,
    //   activate_date: item.fromDate,
    //   expiration_date: item.toDate,
    //   period: item.limitPeriod ? `${item.limitPeriod}Y` : '-',
    //   count: item.limitCount ? `${item.usedCount} / ${item.limitCount}` : 'Unlimited',
    //   user: `${item.activatedUserCount || '-'} ${
    //     !item.isUnlimitedUser ? `/${item.limitUser}` : ''
    //   }`,
    //   status: item.status,
    //   delete: '-',
    // };

    // sessionStorage.setItem('LICENSE_HISTORY_ITEM', JSON.stringify(licenseHistories));
    // window.open(
    //   `/popup/license-detail/${item.id}`,
    //   '_blank',
    //   'toolbar=no,location=no,directories=no,titlebar=no,status=no,menubar=no,scrollbars=no,resizable=no,copyhistory=no,width=900,height=790',
    // );
  };

  return (
    <>
      <label>License Information</label>
      <LicenseInfoBody>
        <LicenseIcon>
          <img src={icons.license_card} />
        </LicenseIcon>
        <HorizontalTable className={'active'}>
          <thead>
            <tr>
              <td colSpan={2}>
                <ProductSection>
                  <img src={logos.logo} />
                  <label>
                    <div style={{ marginTop: `${PRODUCT_TITLE.includes('StroCare') ? '-6px' : 0}` }}>
                      {PRODUCT_TITLE}
                      {PRODUCT_TITLE.includes('StroCare') ? <span className="superscript">&#8482;</span> : <></>}
                      <p>{infoData.version}</p>
                    </div>
                  </label>
                </ProductSection>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>Model Name</th>
              <td>
                <div>
                  {PRODUCT_TITLE}{' '}
                  {PRODUCT_TITLE.includes('StroCare') ? <span className="superscript">&#8482;</span> : <></>}
                </div>
              </td>
            </tr>
            {infoData?.networkType && (
              <tr>
                <th>Product type</th>
                <td>{infoData.companyName ? infoData?.networkType : '-'}</td>
              </tr>
            )}
            <tr>
              <th>License type</th>
              <td>{infoData.companyName ? infoData.type : '-'}</td>
            </tr>
            <tr>
              <th>Company/Department</th>
              <td>{decode(infoData.companyName ? infoData.companyName || '' : '-')}</td>
            </tr>
            <tr>
              <th>Expiration Date</th>
              <td>
                {infoData.companyName
                  ? getLicenseInfoString(infoData.isUnlimitedPeriod, '', `~${infoData.toDate || ''}`)
                  : '-'}
              </td>
            </tr>
            <tr>
              <th>Number of user available</th>
              <td>
                {infoData.companyName
                  ? getLicenseInfoString(
                      infoData.isUnlimitedUser,
                      `${infoData.activatedUserCount}`,
                      `/${infoData.limitUser === 0 ? 0 : infoData.limitUser || ''}`,
                    )
                  : '-'}
              </td>
            </tr>
            <tr>
              <th>Number of analysis available</th>
              {infoData.companyName ? (
                <NOTavailable onClick={() => infoData.id !== null && moveToDetailItemPage(infoData)}>
                  <div>
                    {getLicenseInfoString(
                      infoData.isUnlimitedCount,
                      `${infoData.usedCount}`,
                      `/${infoData.limitCount || ''}`,
                    )}
                    <SearchButton diameter={16} />
                  </div>
                </NOTavailable>
              ) : (
                <td>-</td>
              )}
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}>Copyright 2021. Heuron. All rights reserved.</td>
            </tr>
          </tfoot>
        </HorizontalTable>
      </LicenseInfoBody>
    </>
  );
};

export default InfoPage;
